import React from "react";
import { Check, X } from "lucide-react";
import { SERVICE_TREE } from "@/lib/services";

// `selected` is array of codes. `supplies` is { code: boolean }. onChange(selected, supplies).
export default function ServicePicker({ selected = [], supplies = {}, onChange, showSupplies = true }) {
  const has = (code) => selected.includes(code);
  const anyOf = (codes) => codes.some((c) => has(c));

  const toggleService = (code) => {
    const set = new Set(selected);
    const nextSupplies = { ...supplies };
    if (set.has(code)) {
      set.delete(code);
      delete nextSupplies[code];
    } else {
      set.add(code);
      if (!(code in nextSupplies)) nextSupplies[code] = true;
    }
    onChange(Array.from(set), nextSupplies);
  };

  const setSupply = (code, value) => {
    onChange(selected, { ...supplies, [code]: value });
  };

  const SupplyToggle = ({ code }) => (
    <div
      className="flex items-center gap-1 ml-2 bg-white rounded-full border border-[#1E3F33]/15 p-0.5"
      data-testid={`supply-toggle-${code}`}
    >
      <span className="text-[10px] uppercase tracking-wider text-[#8A9A86] px-1.5">Supplies</span>
      <button
        type="button"
        data-testid={`supply-yes-${code}`}
        onClick={(e) => { e.stopPropagation(); setSupply(code, true); }}
        className={`text-[11px] rounded-full px-2 py-0.5 inline-flex items-center gap-0.5 transition-colors ${
          supplies[code] === true ? "bg-[#2D6A4F] text-white" : "text-[#5C6B64] hover:bg-[#F0EFEA]"
        }`}
      ><Check className="w-3 h-3" /> Yes</button>
      <button
        type="button"
        data-testid={`supply-no-${code}`}
        onClick={(e) => { e.stopPropagation(); setSupply(code, false); }}
        className={`text-[11px] rounded-full px-2 py-0.5 inline-flex items-center gap-0.5 transition-colors ${
          supplies[code] === false ? "bg-[#E63946] text-white" : "text-[#5C6B64] hover:bg-[#F0EFEA]"
        }`}
      ><X className="w-3 h-3" /> No</button>
    </div>
  );

  const Chip = ({ code, label }) => (
    <div className="inline-flex items-center flex-wrap gap-y-1">
      <button
        type="button"
        data-testid={`svc-${code}`}
        onClick={() => toggleService(code)}
        className={`text-sm rounded-full px-3 py-1.5 border transition-colors ${
          has(code)
            ? "bg-[#1E3F33] text-white border-[#1E3F33]"
            : "bg-white text-[#1E3F33] border-[#1E3F33]/15 hover:border-[#1E3F33]/40"
        }`}
      >
        {label}
      </button>
      {has(code) && showSupplies && <SupplyToggle code={code} />}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="service-picker">
      {SERVICE_TREE.map((top) => {
        if (top.code) {
          return (
            <div key={top.key} className={`border rounded-xl p-4 transition-colors ${has(top.code) ? "border-[#CC5A3A]/40 bg-[#CC5A3A]/5" : "border-[#1E3F33]/10"}`}>
              <Chip code={top.code} label={top.label} />
            </div>
          );
        }
        if (top.children) {
          const codes = top.children.map((c) => c.code);
          return (
            <div key={top.key} className={`border rounded-xl p-4 transition-colors ${anyOf(codes) ? "border-[#CC5A3A]/40 bg-[#CC5A3A]/5" : "border-[#1E3F33]/10"}`}>
              <div className="font-heading text-sm font-medium text-[#1E3F33] mb-3">{top.label}</div>
              <div className="flex flex-wrap gap-x-3 gap-y-2">
                {top.children.map((c) => <Chip key={c.code} code={c.code} label={c.label} />)}
              </div>
            </div>
          );
        }
        if (top.groups) {
          const allCodes = top.groups.flatMap((g) => g.children.map((c) => c.code));
          return (
            <div key={top.key} className={`border rounded-xl p-4 transition-colors ${anyOf(allCodes) ? "border-[#CC5A3A]/40 bg-[#CC5A3A]/5" : "border-[#1E3F33]/10"}`}>
              <div className="font-heading text-sm font-medium text-[#1E3F33] mb-3">{top.label}</div>
              <div className="space-y-3">
                {top.groups.map((g) => (
                  <div key={g.key}>
                    <div className="text-xs uppercase tracking-wider text-[#8A9A86] mb-1.5">{g.label}</div>
                    <div className="flex flex-wrap gap-x-3 gap-y-2">
                      {g.children.map((c) => <Chip key={c.code} code={c.code} label={c.label} />)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}
